import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Calendar, User, ArrowRight, Edit3, Trash2, Download } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import './ProposalList.css';

const ProposalList = () => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProposals();
  }, []);

  const fetchProposals = async () => {
    try {
      const response = await apiService.getProposals();
      // Handle both new MongoDB format and backward compatibility
      const proposalsData = response.data.proposals || response.data || [];
      setProposals(proposalsData);
      console.log('Fetched proposals:', proposalsData);
    } catch (error) {
      console.error('Error fetching proposals:', error);
      toast.error('Error loading proposals');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const extractClientName = (proposal) => {
    // Handle both MongoDB and memory formats
    if (proposal.client_name) {
      return proposal.client_name;
    }
    if (proposal.clientName) {
      return proposal.clientName;
    }
    if (proposal.filename) {
      // Extract client name from filename (e.g., "V_Mart_Analytical_Thinking_Proposal.pptx" -> "V Mart")
      const name = proposal.filename.replace(/_/g, ' ').replace('.pptx', '');
      return name.split(' ').slice(0, 2).join(' '); // Take first two words
    }
    return 'Unknown Client';
  };

  const getProposalFilename = (proposal, format = 'pptx') => {
    // Handle both MongoDB and memory formats
    if (proposal.filename) {
      return proposal.filename;
    }
    
    // Use MongoDB filenames if available
    if (format === 'pdf' && proposal.pdf_filename) {
      return proposal.pdf_filename;
    }
    if (format === 'pptx' && proposal.pptx_filename) {
      return proposal.pptx_filename;
    }
    if (format === 'txt' && proposal.txt_filename) {
      return proposal.txt_filename;
    }
    if (format === 'md' && proposal.md_filename) {
      return proposal.md_filename;
    }
    
    // Fallback to generated filename
    const clientName = extractClientName(proposal);
    return `${clientName.replace(/\s+/g, '_')}_Training_Proposal.${format}`;
  };

  const getProposalDate = (proposal) => {
    // Handle both MongoDB and memory formats
    if (proposal.created_at) {
      return proposal.created_at;
    }
    if (proposal.generation_date) {
      return proposal.generation_date;
    }
    return new Date().toISOString();
  };

  const handleDeleteProposal = async (proposalId, proposalName) => {
    // Show confirmation dialog
    const confirmed = window.confirm(`Are you sure you want to delete "${proposalName}"? This action cannot be undone.`);
    
    if (!confirmed) {
      return;
    }

    try {
      const response = await apiService.deleteProposal(proposalId);
      
      if (response.data.error) {
        toast.error(`Failed to delete proposal: ${response.data.message}`);
        return;
      }
      
      toast.success(`Proposal "${proposalName}" deleted successfully`);
      
      // Refresh the proposals list
      fetchProposals();
      
    } catch (error) {
      console.error('Error deleting proposal:', error);
      toast.error('Failed to delete proposal');
    }
  };

  const handleDownloadProposal = async (filename, format) => {
    try {
      const response = await apiService.downloadProposal(filename, format);
      
      // Create a blob from the response
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 
              format === 'pptx' ? 'application/vnd.openxmlformats-officedocument.presentationml.presentation' : 
              'text/plain' 
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename.replace(/\.[^/.]+$/, '') + '.' + format;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${format.toUpperCase()} file successfully!`);
    } catch (error) {
      console.error('Error downloading proposal:', error);
      toast.error(`Failed to download ${format.toUpperCase()} file`);
    }
  };

  const handleDownloadProposalV2 = async (proposal) => {
    try {
      const clientName = extractClientName(proposal);
      const filename = `${clientName.replace(/\s+/g, '_')}_Training_Proposal.pptx`;
      const backendBase = (process.env.REACT_APP_BACKEND_URL || 'https://propulseapi.brainayan.com').replace(/\/$/, '');
      const url = `${backendBase}/api/proposals/download-v2/${filename}?format=pptx`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${clientName.replace(/\s+/g, '_')}_Training_Proposal_v2.pptx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      toast.success('Downloaded PPTX v2 successfully!');
    } catch (error) {
      console.error('Error downloading PPTX v2:', error);
      toast.error('Failed to download PPTX v2');
    }
  };

  if (loading) {
    return (
      <div className="proposal-list-container">
        <div className="loading">Loading proposals...</div>
      </div>
    );
  }

  return (
    <div className="proposal-list-container">
      <div className="proposal-list-header">
        <div className="header-content">
          <h1>📁 Generated Proposals</h1>
          <p>Click on any proposal to view, edit, and add comments</p>
        </div>
        <button onClick={fetchProposals} className="refresh-button">
          🔄 Refresh
        </button>
      </div>

      <div className="proposal-grid">
        {proposals.length === 0 ? (
          <div className="no-proposals">
            <FileText size={48} />
            <h3>No proposals found</h3>
            <p>Generate your first proposal to see it here</p>
            <Link to="/proposal-generator" className="btn-primary">
              Generate Proposal
            </Link>
          </div>
        ) : (
          proposals.map((proposal) => (
            <div key={proposal.id || proposal._id || proposal.clientName} className="proposal-card">
              <Link 
                to={`/proposal-viewer/${proposal.id || proposal._id}`}
                className="proposal-card-content"
              >
              <div className="proposal-card-header">
                <FileText className="proposal-icon" />
                <div className="proposal-info">
                  <h3>{extractClientName(proposal)}</h3>
                  <p className="filename">{getProposalFilename(proposal)}</p>
                </div>
                {/* <ArrowRight className="arrow-icon" /> */}
              </div>
              
              <div className="proposal-card-details">
                <div className="detail-item">
                  <Calendar size={16} />
                  <span>{formatDate(getProposalDate(proposal))}</span>
                </div>
                <div className="detail-item">
                  <User size={16} />
                  <span>{extractClientName(proposal)}</span>
                </div>
                {proposal.pipeline_type && (
                  <div className="detail-item">
                    <span>Pipeline: {proposal.pipeline_type}</span>
                  </div>
                )}
              </div>

              {/* <div className="proposal-card-actions">
                <span className="status-badge">Ready to Edit</span>
              </div> */}
              </Link>
              
              {/* <div className="proposal-card-edit-actions">
                <Link 
                  to={`/proposal-editor/${proposal.id || proposal._id}`}
                  className="edit-button"
                >
                  <Edit3 size={16} />
                  Edit
                </Link>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteProposal(proposal.id || proposal._id, extractClientName(proposal));
                  }}
                  className="delete-button"
                  title="Delete proposal"
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              </div> */}
              
              <div className="proposal-card-download-actions">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDownloadProposal(getProposalFilename(proposal, 'pdf'), 'pdf');
                  }}
                  className="download-button"
                  title="Download PDF"
                >
                  <Download size={16} />
                  PDF
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDownloadProposal(getProposalFilename(proposal, 'md'), 'md');
                  }}
                  className="download-button"
                  title="Download DOCX"
                >
                  <Download size={16} />
                  MD
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDownloadProposal(getProposalFilename(proposal, 'pptx'), 'pptx');
                  }}
                  className="download-button"
                  title="Download PPTX"
                >
                  <Download size={16} />
                  PPTX
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDownloadProposalV2(proposal);
                  }}
                  className="download-button"
                  title="Download PPTX v2"
                >
                  <Download size={16} />
                  PPTX v2
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProposalList; 